// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The only thing Sidebar knows about. One import, one line, one marked
// comment block over there — everything else lives in this folder.
//
// It renders as a small floating launcher, portalled to <body> and pinned to
// the bottom-left corner. It was a row inside the sidebar until 2026-09-04, and
// that was wrong for the one reason that mattered: every fullScreen <Modal>
// (z-[70], fixed inset-0) covers the sidebar completely, and the vendor-invoice
// review screen is one of those. The person who files the most bugs could not
// reach the button from the screen she spends her day on — she had to close the
// invoice first, and by then the viewport being photographed was the invoice
// LIST, not the invoice. Portalled out of the sidebar's subtree the launcher
// escapes that.
//
// It sat at z-[80] and that was only half the fix: above a plain Modal, below or level
// with every other overlay in the app, so on those the click went to the popup's backdrop
// and closed it instead (ClickUp 14yj85ny3xf). It is now z-[110], above all of them. It
// does float over a blocking ActionDialog, which the earlier level deliberately avoided.
// that was the wrong trade: a refusal dialog is exactly the screen someone wants to
// photograph.
//
// The old objection to a floating button — that anchored over the page it
// covered content on every screen — is answered by the geometry: 40px inset
// 12px spans x=12..52, inside even the COLLAPSED sidebar's 64px column. On a
// normal screen it still costs no page area; it only overlays a modal, which is
// the whole point.
//
// The report LIST is its own screen (BugReportsPage, routed at /bug-reports).
// It started as a panel in here to keep the shared-file count down, and that
// was fine for one report and unusable past that — no room for a screenshot,
// no filters, no paging. It cost one marked block in App.tsx to fix, which is
// the right trade. This widget still owns the two ACTIONS: report the screen
// you are on, or go and look at the list.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bug, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getUnseenStatusCount } from '../../lib/bugReportApi';
import { BugReportModal } from './BugReportModal';
import { entityFor, pageLabelFor } from './pageLabels';
import { useScreenshot, type Capture } from './useScreenshot';

// Two switches, both of which must be false-able independently:
//  - this build-time flag hides the button, for a client build that ships
//    without the feature at all;
//  - the server's BUG_REPORTER_ENABLED makes every route 404, which the probe
//    below notices and treats as "not there".
// Absent means ON: the flag exists to switch the feature OFF in month two, and
// a deploy that forgets to set it must not silently lose the feature early.
const BUILD_ENABLED = process.env.REACT_APP_BUG_REPORTER_ENABLED !== 'false';

const UNSEEN_POLL_MS = 5 * 60 * 1000;

export const BugReporter: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { capture, capturing } = useScreenshot();

  // null = not yet probed. Rendering nothing until the probe answers keeps the
  // button from flashing in and out on a deployment where the server has it off.
  const [available, setAvailable] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [shot, setShot] = useState<Capture | null>(null);
  const [shotError, setShotError] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // The panel opens beside the launcher, cleared of its right edge and
  // bottom-aligned with it. Read off the launcher's own rect when it opens, so
  // there is no position to keep in sync with the CSS above it.
  const [anchor, setAnchor] = useState({ left: 60, bottom: 12 });

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAnchor({
        left: Math.round(rect.right + 8),
        bottom: Math.round(window.innerHeight - rect.bottom),
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  // Once a 404 has answered "not there", every later poll would 404 again for
  // no reason — the switch doesn't flip mid-session. A ref, not state: reading
  // `available` here shouldn't recreate refreshUnseen and re-fire the effect
  // below, which would leave a duplicate interval running.
  const availableRef = useRef<boolean | null>(null);

  const refreshUnseen = useCallback(async () => {
    if (availableRef.current === false) return;
    try {
      const res = await getUnseenStatusCount();
      setUnseen(res.data.count);
      setAvailable(true);
      availableRef.current = true;
    } catch (err: any) {
      // 404 is the server saying the feature is switched off — that is an
      // answer, not a failure, and the widget removes itself. Anything else
      // (offline, 500) leaves the button alone: a network blip must not make
      // the way to report bugs disappear.
      if (err?.status === 404) {
        setAvailable(false);
        availableRef.current = false;
      } else {
        setAvailable((prev) => (prev === null ? true : prev));
      }
    }
  }, []);

  useEffect(() => {
    if (!BUILD_ENABLED || !user) return;
    refreshUnseen();
    const timer = window.setInterval(refreshUnseen, UNSEEN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshUnseen, user]);

  // Click-away, so the panel behaves like every other popover in the app.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const startReport = async () => {
    // Close FIRST, and wait a frame: html2canvas photographs the live DOM, so an
    // open panel would end up in the picture of the screen it is covering.
    setOpen(false);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const { capture: taken, error } = await capture();
    setShot(taken);
    setShotError(error);
    setModalOpen(true);
  };

  if (!BUILD_ENABLED || !user || available === false) return null;

  const { entityType, entityId } = entityFor(location.pathname);

  return createPortal(
    // Portalled to <body> rather than rendered where it is mounted: a fullScreen
    // Modal covers the sidebar, and this has to be pressable from on top of one.
    //
    // The whole widget stays invisible to the capture — otherwise the launcher,
    // and the panel it opens, appear in the reporter's own screenshot.
    // html2canvas honours the attribute while CLONING the document, so it holds
    // on the foreignObject path too (see useScreenshot.ts).
    <div
      data-html2canvas-ignore="true"
      // ABOVE EVERY POPUP THE APP OPENS (ClickUp 14yj85ny3xf). z-[80] was above a plain
      // Modal and below everything else: the attachment preview (z-[80], and later in the
      // DOM, so it won), ActionDialog (z-[95]), the quotation's focus mode
      // (z-[90]). On any of those the click hit the popup's own
      // backdrop instead, which closed it: the way to report a broken popup was to destroy
      // the broken popup. Reporting is most needed exactly when something is stuck, so this
      // is now the highest thing on the screen bar the session-expired blocker (z-[999]),
      // which means you are logged out and this widget renders nothing anyway.
      //
      // Keep BugReportModal's own overlay at this level too, or the form opens behind the
      // popup it was launched over. The zIndexAboveEveryPopup test fails if a new overlay
      // is added above this line.
      className="fixed bottom-3 left-3 z-[110] no-print"
    >
      {open && (
        <div
          ref={panelRef}
          style={{
            left: anchor.left,
            bottom: anchor.bottom,
            maxWidth: `calc(100vw - ${anchor.left + 24}px)`,
          }}
          className="fixed z-40 w-80 max-h-[70vh] flex flex-col
                     rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200 shrink-0">
            <span className="text-xs font-semibold text-slate-700">Found a problem?</span>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            >
              <X size={14} />
            </button>
          </div>

          <div className="p-3 space-y-2">
            <button
              type="button"
              onClick={startReport}
              disabled={capturing}
              className="w-full rounded-lg bg-slate-800 px-3 py-2.5 text-sm font-medium text-white hover:bg-slate-900
                         disabled:opacity-60 disabled:cursor-not-allowed transition text-left"
            >
              {capturing ? 'Capturing this screen…' : 'Report a bug on this screen'}
              <span className="block text-xs font-normal text-slate-300 mt-0.5">
                Takes a picture of this page so you can circle the problem
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setUnseen(0);
                navigate('/bug-reports');
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-medium text-slate-700
                         hover:bg-slate-50 transition text-left flex items-center justify-between"
            >
              See all reported bugs
              {unseen > 0 && (
                <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                  {unseen} updated
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Hidden while the report form is up. The form is a Modal at z-[70] and
          this launcher sits above that, so left rendered it would float over
          its own form. */}
      {!modalOpen && (
        <button
          ref={buttonRef}
          type="button"
          aria-label="Report a bug"
          title="Report a bug"
          onClick={() => setOpen((prev) => !prev)}
          className="relative h-10 w-10 inline-flex items-center justify-center rounded-full
                     bg-slate-800 text-white shadow-lg hover:bg-slate-900 transition"
        >
          <Bug size={18} className="shrink-0" />
          {unseen > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-rose-600 ring-2 ring-white" />
          )}
        </button>
      )}

      <BugReportModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        capture={shot}
        captureError={shotError}
        context={{
          path: location.pathname,
          search: location.search,
          pageLabel: pageLabelFor(location.pathname),
          entityType,
          entityId,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}×${window.innerHeight}`,
        }}
        onFiled={(ticketNumber) => {
          toast.success(`Thank you — logged as ${ticketNumber}.`);
          refreshUnseen();
        }}
      />
    </div>,
    document.body
  );
};
